/**
 * Navigation: one definition for the sidebar, the mobile menu and the command
 * palette. Items the signed-in user cannot use are hidden (see lib/access.ts).
 */

import type { Component } from "vue";
import {
  AppWindow,
  Blocks,
  Images,
  MonitorSmartphone,
  Share2,
  Webhook,
  Activity,
  BookOpen,
  Bot,
  CheckCheck,
  Cog,
  Database,
  FileClock,
  History,
  Home,
  Inbox,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  ListTodo,
  MessageSquareText,
  Network,
  Plug,
  PlugZap,
  RefreshCw,
  Search,
  Server,
  ShieldCheck,
  Smile,
  Sparkles,
  Users,
  Workflow,
  Wrench,
  Zap,
} from "@lucide/vue";
import { t } from "@/copy/strings";
import type { Requirement } from "@/lib/access";

export interface NavItem {
  label: string;
  /** Short label for the phone tab bar (default: label). */
  shortLabel?: string;
  to: string;
  icon: Component;
  requires?: Requirement;
  /** Match nested paths for the active state (default: prefix match). */
  exact?: boolean;
  /** Extra path prefixes that mark this item active. */
  alsoActive?: string[];
  badge?: "approvals" | "inbox";
}

export interface NavSection {
  id: string;
  /** Labelled sections collapse in the sidebar (one open at a time, see AppNav)… */
  label?: string;
  /** …unless `collapsible: false`: then the label is a plain heading and the items always show. */
  collapsible?: boolean;
  /** Pages of this section that are not items (an overview): visiting them opens the section. */
  paths?: string[];
  items: NavItem[];
}

export const NAV: NavSection[] = [
  {
    // Find out: the front door, the assistant and what Smake knows.
    id: "main",
    items: [
      { label: t.nav.home, to: "/", icon: Home, exact: true },
      { label: t.nav.ask, to: "/ask", icon: Sparkles, requires: { capability: "knowledge.read" } },
      { label: t.nav.schorschi, to: "/schorschi", icon: Smile, requires: { capability: "schorschi.read" } },
      { label: t.nav.knowledge, to: "/knowledge", icon: BookOpen, requires: { capability: "knowledge.read" }, alsoActive: ["/k/"] },
    ],
  },
  {
    // Get things done, and follow what is running or waiting.
    id: "work",
    label: t.nav.sectionWork,
    collapsible: false,
    items: [
      { label: t.nav.actions, to: "/actions", icon: Zap, alsoActive: ["/runbooks"] },
      { label: t.nav.tools, to: "/tools", icon: Wrench, alsoActive: ["/apps"] },
      { label: "Todos", to: "/todos", icon: ListTodo },
      { label: "Agents", to: "/agents", icon: Workflow, requires: { capability: "agents.read" } },
      { label: t.nav.jobs, shortLabel: "Jobs", to: "/jobs", icon: ListChecks },
      { label: t.nav.approvals, to: "/approvals", icon: CheckCheck, badge: "approvals", alsoActive: ["/changes", "/operations"] },
    ],
  },
  {
    // Your accounts and keys, your things, your records.
    id: "you",
    label: t.nav.sectionYou,
    items: [
      { label: t.nav.connections, to: "/connections", icon: Plug },
      { label: t.nav.connectedApps, to: "/connected-apps", icon: AppWindow },
      { label: "AI gateway", to: "/ai-gateway", icon: Bot, requires: { capability: "ai.exec" } },
      { label: t.nav.files, to: "/files", icon: Images },
      { label: t.nav.shares, to: "/shares", icon: Share2, requires: { capability: "share.read" } },
      { label: t.nav.remote, to: "/remote", icon: MonitorSmartphone, requires: { capability: "remote.exec" } },
      { label: t.nav.webhooks, to: "/webhooks", icon: Webhook, requires: { capability: "webhooks.write" } },
      { label: t.nav.activity, to: "/activity", icon: History },
      { label: t.nav.audit, to: "/audit", icon: ShieldCheck, requires: { audit: true } },
    ],
  },
  {
    id: "admin",
    label: t.nav.sectionAdmin,
    paths: ["/admin"],
    items: [
      { label: t.nav.access, to: "/admin/access", icon: KeyRound, requires: { adminArea: "access" } },
      { label: t.nav.users, to: "/admin/users", icon: Users, requires: { adminArea: "users" } },
      { label: t.nav.datasources, to: "/admin/datasources", icon: Database, requires: { adminArea: "datasources" } },
      { label: t.nav.connectionProviders, to: "/admin/connection-providers", icon: PlugZap, requires: { adminArea: "connections" } },
      { label: t.nav.mcpClients, to: "/admin/mcp-clients", icon: Blocks, requires: { adminArea: "access" } },
      { label: t.nav.ai, to: "/admin/ai", icon: Bot, requires: { adminArea: "ai" } },
      { label: t.nav.adminRunbooks, to: "/admin/runbooks", icon: FileClock, requires: { adminArea: "runbooks" } },
      { label: t.nav.feedback, to: "/admin/feedback", icon: MessageSquareText, requires: { capability: "feedback.admin" } },
      { label: t.nav.settings, to: "/admin/settings", icon: Cog, requires: { adminArea: "settings" } },
      { label: t.nav.system, to: "/admin/system", icon: Server, requires: { adminArea: "system" } },
    ],
  },
];

/**
 * Administration that lives inside another area (so the sidebar marks that
 * area, not Administration): listed on the Administration overview only.
 */
export const ADMIN_ELSEWHERE: NavItem[] = [
  { label: t.nav.knowledgeSources, to: "/knowledge/sources", icon: RefreshCw, requires: { capability: "knowledge.admin" } },
];

/** Every destination the navigation knows (sidebar sections plus ADMIN_ELSEWHERE): the command palette's "Go to". */
export function navDestinations(): NavItem[] {
  return [...NAV.flatMap((s) => s.items), ...ADMIN_ELSEWHERE];
}

/** Secondary navigation inside Knowledge. */
export const KNOWLEDGE_NAV: NavItem[] = [
  { label: t.nav.overview, to: "/knowledge", icon: LayoutDashboard, exact: true, alsoActive: ["/knowledge/lineage/"] },
  { label: t.nav.search, to: "/knowledge/search", icon: Search },
  { label: t.nav.graphViews, to: "/knowledge/views", icon: Network },
  { label: t.nav.inbox, to: "/knowledge/inbox", icon: Inbox, badge: "inbox" },
  { label: t.nav.changeLog, to: "/knowledge/changes", icon: Activity },
  { label: t.nav.sources, to: "/knowledge/sources", icon: RefreshCw, requires: { capability: "knowledge.admin" } },
  { label: t.nav.maintenance, to: "/knowledge/maintenance", icon: Wrench, requires: { capability: "knowledge.admin" } },
];

/** Anything that can be matched against the current path (nav items, page tabs). */
export interface PathMatchable {
  to: string;
  exact?: boolean;
  alsoActive?: string[];
}

function matchLength(item: PathMatchable, path: string): number {
  if (item.exact && path === item.to) return item.to.length + 1000;
  if (!item.exact && (path === item.to || path.startsWith(`${item.to}/`))) return item.to.length;
  const extra = item.alsoActive?.find((p) => path.startsWith(p));
  return extra ? extra.length : -1;
}

/** The single most specific item matching the path (so nested pages highlight one entry). */
export function activeItem<T extends PathMatchable>(items: T[], path: string): T | null {
  let best: T | null = null;
  let bestLength = -1;
  for (const item of items) {
    const length = matchLength(item, path);
    if (length > bestLength) {
      best = item;
      bestLength = length;
    }
  }
  return best;
}

/**
 * Phones and tablets: the bottom tab bar shows the first four of these the
 * user may use (by path into NAV, so gating and labels stay in one place);
 * everything else is behind "More".
 */
export const MOBILE_TABS = ["/", "/knowledge", "/schorschi", "/approvals", "/tools", "/jobs", "/actions"];
export const MOBILE_TAB_COUNT = 4;

/** NAV items for the bottom tab bar, in MOBILE_TABS order, filtered by what the user may use. */
export function mobileTabs(allowed: (item: NavItem) => boolean): NavItem[] {
  const all = NAV.flatMap((s) => s.items);
  return MOBILE_TABS.map((to) => all.find((i) => i.to === to))
    .filter((i): i is NavItem => Boolean(i) && allowed(i!))
    .slice(0, MOBILE_TAB_COUNT);
}
